import { useState, useEffect, useCallback } from "react";
import { collection, query, where, getCountFromServer } from "firebase/firestore";
import { db } from "@/lib/firebase";

export interface OrgUserCounts {
  totalUsers: number | null;
  activeUsers: number | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

interface OrgUserCountsState {
  orgId: string | undefined;
  totalUsers: number | null;
  activeUsers: number | null;
  loading: boolean;
  error: string | null;
}

function initialState(orgId: string | undefined): OrgUserCountsState {
  return { orgId, totalUsers: null, activeUsers: null, loading: !!orgId, error: null };
}

/**
 * Org-wide total/active user counts via scoped `getCountFromServer` aggregate
 * queries — accurate above the useOrgEndUsers 500-doc slice cap. Runs two
 * queries on org load and on an explicit `refresh()` call only, never per
 * render or per snapshot. `null` means "unavailable" (loading or errored);
 * callers must not substitute 0 or the slice length for it.
 *
 * State is tagged with the org it belongs to, exactly like useOrgEndUsers:
 * React renders a component with a new `orgId` prop before this hook's
 * effect resets state, so the return statement masks a mismatched org's
 * counts synchronously during render rather than waiting for the effect.
 */
export function useOrgUserCounts(orgId: string | undefined): OrgUserCounts {
  const [state, setState] = useState<OrgUserCountsState>(() => initialState(orgId));
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setState(initialState(orgId));

    if (!orgId) {
      return;
    }

    const ref = collection(db, "endUsers");
    const totalQuery = query(ref, where("orgId", "==", orgId));
    const activeQuery = query(ref, where("orgId", "==", orgId), where("status", "==", "active"));

    Promise.all([getCountFromServer(totalQuery), getCountFromServer(activeQuery)])
      .then(([totalSnap, activeSnap]) => {
        // A slower request for an org the caller has already navigated away
        // from must not overwrite the newer org's counts.
        if (cancelled) return;
        setState({
          orgId,
          totalUsers: totalSnap.data().count,
          activeUsers: activeSnap.data().count,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching org user counts:", err);
        setState({ orgId, totalUsers: null, activeUsers: null, loading: false, error: "Failed to load user counts." });
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, refreshToken]);

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  const isCurrent = state.orgId === orgId;
  return {
    totalUsers: isCurrent ? state.totalUsers : null,
    activeUsers: isCurrent ? state.activeUsers : null,
    loading: isCurrent ? state.loading : !!orgId,
    error: isCurrent ? state.error : null,
    refresh,
  };
}
